import { Component, ErrorInfo, ReactNode } from "react";
import * as Sentry from "@sentry/react";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  /** 顯示在錯誤頁面右上角的標籤（用於區分巢狀 boundary） */
  label?: string;
}

interface State {
  hasError: boolean;
  error?: Error;
}

/**
 * 全域錯誤邊界：攔截子元件的 render error，避免整個 app 白屏
 *
 * - 發生錯誤時送出 Sentry event（若有初始化）
 * - 顯示 fallback UI，提供「重試」按鈕
 *
 * 使用方式：
 * ```tsx
 * <ErrorBoundary label="Checkout">
 *   <CheckoutPage />
 * </ErrorBoundary>
 * ```
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // 送到 Sentry（若已初始化）
    if (import.meta.env.VITE_SENTRY_DSN) {
      Sentry.captureException(error, {
        extra: { componentStack: info.componentStack, label: this.props.label },
      });
    }
    // 也打到 console，方便本地除錯
    console.error(`[ErrorBoundary${this.props.label ? `:${this.props.label}` : ""}]`, error, info);
  }

  handleReset = (): void => {
    this.setState({ hasError: false, error: undefined });
  };

  render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;

      return (
        <div
          role="alert"
          style={{
            padding: "2rem",
            maxWidth: "32rem",
            margin: "4rem auto",
            fontFamily: "system-ui, -apple-system, sans-serif",
            textAlign: "center",
            color: "#1f2937",
          }}
        >
          <h1 style={{ fontSize: "1.5rem", marginBottom: "0.5rem" }}>
            ⚠️ 發生未預期的錯誤
          </h1>
          {this.props.label && (
            <p style={{ color: "#6b7280", fontSize: "0.875rem" }}>
              區塊：{this.props.label}
            </p>
          )}
          {this.state.error && (
            <pre
              style={{
                marginTop: "1rem",
                padding: "1rem",
                background: "#f3f4f6",
                borderRadius: "0.5rem",
                fontSize: "0.75rem",
                overflow: "auto",
                textAlign: "left",
                color: "#991b1b",
              }}
            >
              {this.state.error.message}
            </pre>
          )}
          <div style={{ marginTop: "1.5rem", display: "flex", gap: "0.5rem", justifyContent: "center" }}>
            <button
              type="button"
              onClick={this.handleReset}
              style={{
                padding: "0.5rem 1rem",
                background: "#2563eb",
                color: "white",
                border: "none",
                borderRadius: "0.375rem",
                cursor: "pointer",
                fontSize: "0.875rem",
              }}
            >
              重試
            </button>
            <button
              type="button"
              onClick={() => window.location.reload()}
              style={{
                padding: "0.5rem 1rem",
                background: "#e5e7eb",
                color: "#1f2937",
                border: "none",
                borderRadius: "0.375rem",
                cursor: "pointer",
                fontSize: "0.875rem",
              }}
            >
              重新整理頁面
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
